<template>
  <div class="hospital-detail">
    <div class="hospital-status">
      <div class="hospital-status-title">Reported Status</div>
      <div class="timestamp greyed-out">{{ timestamp }}</div>
      <div
        v-for="item in statuses"
        :key="item.label"
        :class="{ unknown: !item.confidence }"
        class="status-item"
      >
        <div class="status-count">
          {{ item.value }}
          <StatusDot
            v-if="item.confidence"
            :fillColor="$options.Confidence[item.confidence].color"
          />
        </div>
        <div class="status-label">{{ item.label }}</div>
      </div>
    </div>
    <div class="hospital-sub-details greyed-out">
      <div class="sub-detail-label">Source</div>
      <div class="sub-detail-description">
        {{ source || 'No data available' }}
      </div>
    </div>
    <div class="hospital-sub-details greyed-out">
      <div class="sub-detail-label">Confidence</div>
      <ul class="sub-detail-description">
        <li
          v-for="item in $options.Confidence"
          :key="item.color"
          class="confidence-description"
        >
          <StatusDot :size="12" :fillColor="item.color" />
          <span>{{ item.label }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import StatusDot from '@/components/status-dot';
import formatDistance from 'date-fns/formatDistance';
import get from 'lodash/get';
import { ConfidenceLevel } from '@/store/types';

const Confidence = Object.freeze({
  [ConfidenceLevel.CERTAIN]: {
    color: '#357CEB',
    label: 'Includes verified official reports from DPH or hospital admin',
  },
  [ConfidenceLevel.EDUCATED_ESTIMATION]: {
    color: '#F19938',
    label: 'Includes verified hospital staff reports',
  },
  [ConfidenceLevel.GUESS]: {
    color: '#F15938',
    label:
      'Unverified reports from people on-site, patients, hospital staff, or other',
  },
  [ConfidenceLevel.UNSPECIFIED]: {
    color: '#C4BECD',
    label: 'Confidence of sources is unspecified',
  },
});

const convertSeconds = seconds => {
  if (seconds === null) {
    return '?';
  }
  if (seconds < 60) {
    return '< 1m';
  }
  const min = seconds / 60;
  if (min < 60) {
    return `${min}m`;
  }
  const hours = min / 60;
  return `${Math.round(hours)}h`;
};

export default {
  Confidence,
  components: { StatusDot },
  props: {
    hospitalData: {
      type: Object,
      required: true,
    },
  },
  computed: {
    timestamp() {
      const updatedAt = get(this.hospitalData, 'status.updated_at', null);
      return updatedAt ? `${formatDistance(updatedAt)} ago` : '';
    },
    source() {
      return get(this.hospitalData, 'status.source', null);
    },
    statuses() {
      const waitTime = get(
        this.hospitalData,
        'status.current_wait_time_seconds.value',
        null,
      );
      return [
        {
          label: 'Wait time',
          value: convertSeconds(waitTime),
          confidence: get(
            this.hospitalData,
            'status.current_wait_time_seconds.confidence',
            null,
          ),
        },
        {
          label: 'ICU beds',
          value: get(this.hospitalData, 'status.icu_beds.available.value', '?'),
          confidence: get(
            this.hospitalData,
            'status.icu_beds.available.confidence',
            null,
          ),
        },
        {
          label: 'COVID-19 beds',
          value: get(
            this.hospitalData,
            'status.corona_beds.available.value',
            '?',
          ),
          confidence: get(
            this.hospitalData,
            'status.corona_beds.available.confidence',
            null,
          ),
        },
        {
          label: 'Ventilators',
          value: get(
            this.hospitalData,
            'status.adult_ventilators.available.value',
            '?',
          ),
          confidence: get(
            this.hospitalData,
            'status.adult_ventilators.available.confidence',
            null,
          ),
        },
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
.status-label {
  font-size: 14px;
  color: var(--dark-blue);
}

.greyed-out,
.unknown .status-label {
  color: #a9a9a9;
}

.hospital-status {
  display: grid;
  grid-gap: 12px;
  grid-template-columns: 1fr 1fr;
  margin-bottom: 24px;
  padding-top: 12px;
}

.hospital-status-title {
  font-size: 15px;
}

.status-item {
  align-items: center;
  display: flex;
}

.status-count {
  align-items: center;
  background: var(--black);
  border-radius: 100px;
  color: var(--yellow);
  display: flex;
  font-size: 14px;
  font-weight: bold;
  height: 32px;
  justify-content: center;
  margin-right: 6px;
  position: relative;
  width: 32px;
}

.status-count {
  .status-dot {
    position: absolute;
    right: -6px;
    top: -6px;
  }
}

.unknown .status-count {
  background: #b7b7b7;
  color: var(--black);
}

.hospital-sub-details {
  align-items: baseline;
  border-top: 1px solid var(--gray);
  font-size: 13px;
  display: flex;
  line-height: 1.167;
  padding: 12px 0;
}

.sub-detail-label {
  flex-shrink: 0;
  margin-right: 16px;
  width: 64px;
  font-weight: 600;
  color: #9c9c9c;
}

.sub-detail-description {
  list-style: none;
  margin: 0;
  padding: 0;

  .status-dot {
    flex-shrink: 0;
    margin-right: 4px;
  }
}

.confidence-description {
  align-items: baseline;
  display: flex;

  + .confidence-description {
    margin-top: 4px;
  }
}
</style>
